import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useProgramme } from "@/contexts/ProgrammeContext";
import { AirplaneTilt, ChartLineUp, Bell, Database, PencilSimple, GearSix, SignOut, List, X, FilePdf, CaretDown } from "@phosphor-icons/react";
import { useState, useEffect } from "react";

// Programme sections (China Airlines / Air Niugini) each own Dashboard, Alerts, Reports.
// Data Sources, Overrides and Settings are shared and global.
const PROGRAMME_NAV = {
  CI: [
    { to: "/", icon: ChartLineUp, label: "Dashboard", exact: true },
    { to: "/alerts", icon: Bell, label: "Alerts" },
    { to: "/reports", icon: FilePdf, label: "Reports" },
  ],
  PX: [
    { to: "/px", icon: ChartLineUp, label: "Dashboard", exact: true },
    { to: "/px/alerts", icon: Bell, label: "Alerts" },
    { to: "/px/reports", icon: FilePdf, label: "Reports" },
  ],
};
function hexToRgb(hex) { const h = hex.replace("#", ""); return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(" "); }

const sharedNav = [
  { to: "/data-sources", icon: Database, label: "Data Sources" },
  { to: "/overrides", icon: PencilSimple, label: "Overrides" },
  { to: "/settings", icon: GearSix, label: "Settings" },
];

function ProgrammeNav({ onNavigate }) {
  const { code, setCode, programmes, setProgrammesFromApi } = useProgramme();
  const { api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => { api.get("/programmes").then(r => setProgrammesFromApi(r.data)).catch(() => {}); }, [api, setProgrammesFromApi]);
  // Route ↔ programme sync: landing on /px/* switches the accent/section automatically
  useEffect(() => {
    const onPX = location.pathname === "/px" || location.pathname.startsWith("/px/");
    if (onPX && code !== "PX") setCode("PX");
    if (!onPX && code === "PX" && /^\/(alerts|reports|routes)?$|^\/(alerts|reports|routes)\b/.test(location.pathname)) setCode("CI");
  }, [location.pathname, code, setCode]);
  const linkCls = ({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isActive ? "bg-ci-blue/20 text-white border border-ci-blue/30" : "text-text-secondary hover:text-white hover:bg-white/5"}`;
  return (
    <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="programme-nav">
      {["CI", "PX"].map(pc => {
        const p = programmes[pc]; const open = code === pc;
        return (
          <div key={pc} className="mb-1">
            <button
              type="button"
              data-testid={`programme-switch-${pc.toLowerCase()}`}
              onClick={() => { setCode(pc); navigate(pc === "PX" ? "/px" : "/"); onNavigate?.(); }}
              className={`programme-header ${open ? "active" : ""} w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-syne font-bold uppercase tracking-wider transition-colors`}
              style={{ "--ph-rgb": hexToRgb(p?.accent_hex || "#E8501A") }}
            >
              <span className="flex items-center gap-2">
                <AirplaneTilt size={14} weight="fill" />
                {p?.name || pc}
              </span>
              <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="space-y-1 mt-1" data-testid={`programme-section-${pc.toLowerCase()}`}>
                {PROGRAMME_NAV[pc].map(({ to, icon: Icon, label, exact }) => (
                  <NavLink key={to} to={to} end={exact} onClick={onNavigate}
                    data-testid={`nav-${pc.toLowerCase()}-${label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
                    <Icon size={18} weight="duotone" />{label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div className="pt-3 mt-2 border-t border-[rgba(255,255,255,0.08)]">
        <p className="px-3 pb-1 text-[10px] uppercase tracking-wider text-text-secondary font-semibold">Shared</p>
        {sharedNav.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
            <Icon size={18} weight="duotone" />{label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle-btn"
      className="theme-toggle-btn"
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      style={{
        background:   "transparent",
        border:       "1px solid rgba(255,255,255,0.12)",
        borderRadius: "8px",
        padding:      "6px 10px",
        cursor:       "pointer",
        color:        "#8EAADF",
        fontSize:     "13px",
        display:      "flex",
        alignItems:   "center",
        gap:          "6px",
        transition:   "all 0.2s",
      }}
    >
      {isDark ? (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/>
            <line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/>
            <line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
          Light
        </>
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
          Dark
        </>
      )}
    </button>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { programme } = useProgramme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const title = programme?.code === "PX" ? "PX Fare Tracker" : "CI Fare Tracker";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-navy flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-[rgba(255,255,255,0.08)] bg-navy/50 backdrop-blur-xl fixed h-full z-40">
        <div className="p-6 border-b border-[rgba(255,255,255,0.08)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-ci-orange/20 flex items-center justify-center">
                <AirplaneTilt size={24} weight="duotone" className="text-ci-orange" />
              </div>
              <div>
                <h1 className="font-syne font-bold text-white text-lg tracking-tight" data-testid="app-title">{title}</h1>
                <p className="text-text-secondary text-xs">Auckland Station</p>
              </div>
            </div>
            <ThemeToggle />
          </div>
        </div>
        <ProgrammeNav />
        <div className="p-4 border-t border-[rgba(255,255,255,0.08)]">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-ci-blue/30 flex items-center justify-center text-xs font-bold text-white">
              {user?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.full_name || user?.email}</p>
              <p className="text-text-secondary text-xs capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-text-secondary hover:text-danger rounded-lg hover:bg-danger/10 transition-colors"
          >
            <SignOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <AirplaneTilt size={24} weight="duotone" className="text-ci-orange" />
            <span className="font-syne font-bold text-white">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn" className="text-white p-2">
              {sidebarOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
        {sidebarOpen && (
          <nav className="pb-4 bg-navy border-b border-[rgba(255,255,255,0.08)] flex flex-col max-h-[80vh]">
            <ProgrammeNav onNavigate={() => setSidebarOpen(false)} />
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-sm text-danger w-full">
              <SignOut size={20} /> Sign Out
            </button>
          </nav>
        )}
      </div>

      {/* Main content */}
      <main className="flex-1 lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
